<template>
  <el-dialog
    :visible.sync="visibleProxy"
    :title="dialogTitle"
    width="760px"
    append-to-body
    :close-on-click-modal="false"
  >
    <el-form ref="form" v-loading="detailLoading" :model="form" :rules="formRules" label-width="100px">
      <el-form-item label="日志日期" prop="logDate">
        <el-date-picker
          v-model="form.logDate"
          type="date"
          value-format="yyyy-MM-dd"
          :disabled="isAutoRecord"
          :picker-options="{ disabledDate: disableFuture }"
          style="width: 100%"
        />
      </el-form-item>
      <el-form-item label="工作类型" prop="workType">
        <el-select v-model="form.workType" :disabled="isAutoRecord" style="width: 100%">
          <el-option
            v-for="option in workTypeOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="所属大区" prop="zoneAreaId">
        <AreaCascader
          v-model="form.zoneAreaId"
          :options="zoneAreas"
          :disabled="isAutoRecord"
          clearable
          style="width: 100%"
          @change="changeZoneArea"
        />
      </el-form-item>
      <el-form-item label="具体二级区域" prop="secondaryAreaId">
        <el-select
          v-model="form.secondaryAreaId"
          :disabled="isAutoRecord || !form.zoneAreaId"
          clearable
          style="width: 100%"
        >
          <el-option v-for="area in secondaryAreas" :key="area.uuid" :label="area.areaName" :value="area.uuid" />
        </el-select>
      </el-form-item>
      <el-form-item label="工作内容" prop="workContent">
        <el-input v-model="form.workContent" type="textarea" :rows="4" maxlength="2000" show-word-limit />
      </el-form-item>
      <el-form-item
        label="工作结果"
        prop="workResult"
        :rules="
          !isAutoRecord && form.processType === 'repair_completed'
            ? [{ required: true, whitespace: true, message: '故障维修完成时请填写工作结果', trigger: 'blur' }]
            : []
        "
      >
        <el-input v-model="form.workResult" type="textarea" :rows="3" maxlength="2000" show-word-limit />
      </el-form-item>
      <el-form-item label="材料使用">
        <el-radio-group v-model="form.materialReplacedFlag" @change="changeMaterialReplaced">
          <el-radio label="Y">已更换</el-radio>
          <el-radio label="N">未更换</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item
        v-if="form.materialReplacedFlag === 'Y'"
        label="材料明细"
        prop="materialSummary"
        :rules="
          form.materialReplacedFlag === 'Y'
            ? [{ required: true, whitespace: true, message: '已更换材料时请填写材料明细', trigger: 'blur' }]
            : []
        "
      >
        <el-input
          v-model="form.materialSummary"
          type="textarea"
          :rows="2"
          :disabled="form.materialReplacedFlag !== 'Y'"
          maxlength="1000"
          show-word-limit
        />
      </el-form-item>
      <el-form-item label="处理标记">
        <el-radio-group v-model="form.processType" :disabled="isAutoRecord" @change="changeProcessType">
          <el-radio label="normal">普通记录</el-radio>
          <el-radio label="fault_created">新增故障</el-radio>
          <el-radio label="repair_completed">故障维修完成</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item
        v-if="form.processType !== 'normal'"
        label="故障单编号"
        prop="faultCode"
        :rules="
          !isAutoRecord && form.processType === 'fault_created'
            ? [{ required: true, whitespace: true, message: '新增故障时请填写故障单编号', trigger: 'blur' }]
            : []
        "
      >
        <el-input
          v-model="form.faultCode"
          :disabled="isAutoRecord || form.processType === 'normal'"
          placeholder="故障维修完成登记可不填写故障单编号"
          maxlength="64"
        />
      </el-form-item>
      <el-form-item label="协作人员">
        <el-select v-model="form.workerIds" multiple filterable style="width: 100%" @change="syncWorkerNames">
          <el-option v-for="user in workers" :key="user.userCode" :label="user.capacityName" :value="user.userCode" />
        </el-select>
      </el-form-item>
      <el-form-item label="工作图片">
        <ImageUploader
          ref="imageUploader"
          v-model="form.imageFileIds"
          :max="9"
          tip="最多上传9张，支持 jpg/png 格式"
          :show-zoom="true"
        />
      </el-form-item>
      <template v-if="logId">
        <el-divider />
        <el-form-item label="来源动作">
          <span>{{ form.sourceActionName || '-' }}</span>
        </el-form-item>
        <el-form-item label="主要执行人">
          <span>{{ form.ownerUserName || '-' }}</span>
        </el-form-item>
        <el-form-item label="来源类型">
          <span>{{ form.sourceTypeName || '-' }}</span>
        </el-form-item>
        <el-form-item label="来源明细">
          <span>{{ form.sourceDetailCount || 0 }} 条 / {{ form.deviceCount || 0 }} 台设备</span>
        </el-form-item>
      </template>
      <el-form-item label="具体位置">
        <el-input v-model="form.workLocation" maxlength="500" placeholder="填写具体工作位置（选填）" />
      </el-form-item>
      <el-form-item label="备注说明">
        <el-input
          v-model="form.remark"
          type="textarea"
          maxlength="2000"
          show-word-limit
          placeholder="对方人员、电话、单位等（选填）"
        />
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button @click="visibleProxy = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import ImageUploader from '@/features/components/common-image/ImageUploader.vue';
import { formatDate } from '@/features/common/utils/dateTimeUtil.js';
import { areaService } from '@/features/gnmk/area/area.service.js';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';
import { workLogService } from './work-log.service.js';
import { WORK_TYPE_OPTIONS, normalizeWorkType, normalizeProcessType } from './workLogDict.js';

const createForm = () => ({
  logDate: formatDate(new Date()),
  workType: 'wb',
  zoneAreaId: '',
  secondaryAreaId: '',
  workLocation: '',
  remark: '',
  workContent: '',
  workResult: '',
  materialReplacedFlag: 'N',
  materialSummary: '',
  processType: 'normal',
  faultCreatedFlag: 'N',
  faultCode: '',
  workerIds: [],
  workerNames: [],
  imageFileIds: []
});

export default {
  name: 'WorkLogDialog',
  components: { ImageUploader, AreaCascader },
  props: {
    visible: { type: Boolean, default: false },
    logId: { type: String, default: null }
  },
  data () {
    return {
      form: createForm(),
      workTypeOptions: WORK_TYPE_OPTIONS,
      workers: [],
      areaTree: [],
      detailLoading: false,
      saving: false,
      formRules: {
        logDate: [{ required: true, message: '请选择日志日期', trigger: 'change' }],
        workType: [{ required: true, message: '请选择工作类型', trigger: 'change' }],
        zoneAreaId: [{ required: true, message: '请选择所属大区', trigger: 'change' }],
        workContent: [{ required: true, message: '请输入工作内容', trigger: 'blur' }]
      }
    };
  },
  computed: {
    visibleProxy: {
      get () {
        return this.visible;
      },
      set (value) {
        this.$emit('update:visible', value);
      }
    },
    dialogTitle () {
      return this.logId ? '编辑工作日志' : '新增工作日志';
    },
    zoneAreas () {
      return this.areaTree.map(({ children, ...area }) => area);
    },
    secondaryAreas () {
      const zone = this.areaTree.find(area => area.uuid === this.form.zoneAreaId);
      return zone && Array.isArray(zone.children) ? zone.children : [];
    },
    isAutoRecord () {
      return this.form.sourceType && this.form.sourceType !== 'manual';
    }
  },
  methods: {
    async loadWorkers () {
      const res = await workLogService.workerCandidates();
      this.workers = res.data || [];
    },
    async loadOptions () {
      const areaResult = await areaService.getAreaTree();
      this.areaTree = areaResult.data || [];
    },
    async loadDetail () {
      this.detailLoading = true;
      try {
        const res = await workLogService.getDetail(this.logId);
        this.form = this.normalizeForm(res.data || {});
      } finally {
        this.detailLoading = false;
      }
    },
    normalizeForm (record) {
      return {
        ...createForm(),
        ...record,
        workType: normalizeWorkType(record.workType),
        zoneAreaId: record.zoneAreaId || '',
        zoneAreaName: record.zoneAreaName || '',
        secondaryAreaId: record.secondaryAreaId || '',
        materialReplacedFlag: record.materialReplacedFlag || (record.materialSummary ? 'Y' : 'N'),
        processType: normalizeProcessType(record),
        faultCreatedFlag: record.faultCreatedFlag || (record.faultCode ? 'Y' : 'N'),
        workerIds: this.splitIds(record.workerIds),
        workerNames: this.splitIds(record.workerNames),
        imageFileIds: this.splitIds(record.imageFileIds)
      };
    },
    splitIds (values) {
      if (Array.isArray(values)) return values.filter(Boolean);
      return values ? String(values).split(',').filter(Boolean) : [];
    },
    disableFuture (date) {
      return date.getTime() > new Date().setHours(23, 59, 59, 999);
    },
    syncWorkerNames () {
      this.form.workerNames = this.workers
        .filter(item => this.form.workerIds.includes(item.userCode))
        .map(item => item.capacityName);
    },
    changeZoneArea () {
      if (!this.secondaryAreas.some(area => area.uuid === this.form.secondaryAreaId)) {
        this.form.secondaryAreaId = '';
      }
    },
    changeMaterialReplaced (value) {
      if (value === 'N') this.form.materialSummary = '';
    },
    changeProcessType (value) {
      this.form.faultCreatedFlag = value !== 'normal' && (this.form.faultCode || '').trim() ? 'Y' : 'N';
      this.$nextTick(() => this.$refs.form.clearValidate(['workResult', 'faultCode']));
      if (value === 'normal') this.form.faultCode = '';
    },
    async save () {
      const valid = await new Promise(resolve => this.$refs.form.validate(resolve));
      if (!valid || this.isImageUploading()) return;
      this.saving = true;
      try {
        await workLogService.save({
          uuid: this.logId || undefined,
          logDate: this.form.logDate,
          workType: this.form.workType,
          zoneAreaId: this.form.zoneAreaId,
          secondaryAreaId: this.form.secondaryAreaId,
          workLocation: this.form.workLocation || '',
          remark: this.form.remark || '',
          workContent: this.form.workContent,
          workResult: this.form.workResult,
          materialReplacedFlag: this.form.materialReplacedFlag,
          materialSummary: this.form.materialSummary,
          processType: this.form.processType,
          faultCreatedFlag: this.form.processType !== 'normal' && (this.form.faultCode || '').trim() ? 'Y' : 'N',
          faultCode: this.form.processType === 'normal' ? '' : this.form.faultCode,
          workerIds: this.form.workerIds,
          workerNames: this.form.workerNames,
          imageFileIds: this.form.imageFileIds
        });
        this.$message.success('保存成功');
        this.$emit('success');
        this.visibleProxy = false;
      } finally {
        this.saving = false;
      }
    },
    isImageUploading () {
      if (!this.$refs.imageUploader || !this.$refs.imageUploader.isUploading()) return false;
      this.$message.warning('图片上传中，请稍候再保存');
      return true;
    }
  },
  async created () {
    await Promise.all([this.loadWorkers(), this.loadOptions()]);
    if (this.logId) await this.loadDetail();
  }
};
</script>

<style lang="scss" scoped>
.el-form {
  padding: 10px 20px 0;
}
</style>
